<template>
  <div id="tacho-file-analysis">
    <!-- Hidden picker used by the dropzone. -->
    <input
      v-if="!detailOnly"
      ref="fileInput"
      type="file"
      class="tfa-file-input"
      accept=".ddd,.DDD"
      @change="onFileChange"
    >

    <!-- Drivers panel: company drivers with their stored-data coverage (layout after the
         legacy "Rozliczenia kierowców" workstation) -->
    <section
      v-if="!detailOnly"
      class="tfa-card tfa-drivers"
    >
      <div class="tfa-drivers-head">
        <div>
          <span class="tfa-eyebrow">{{ $t('components.tachoAnalysis.drivers.eyebrow') }}</span>
          <h2 class="tfa-page-title">
            {{ $t('components.tachoAnalysis.drivers.title') }}
          </h2>
          <p class="tfa-page-sub">
            {{ $t('components.tachoAnalysis.drivers.sub') }}
          </p>
        </div>
        <div class="tfa-drivers-controls">
          <button
            type="button"
            class="tfa-btn tfa-btn--primary"
            @click="toggleUpload"
          >
            <i class="bi bi-cloud-arrow-up" /> {{ $t('components.tachoAnalysis.drivers.uploadButton') }}
          </button>
          <button
            type="button"
            class="tfa-btn"
            @click="refreshDrivers"
          >
            <i class="bi bi-arrow-clockwise" /> {{ $t('components.tachoAnalysis.drivers.refresh') }}
          </button>
        </div>
      </div>

      <div class="tfa-stats">
        <div>
          <strong>{{ stats.active }}</strong>
          <span>{{ $t('components.tachoAnalysis.drivers.stats.active') }}</span>
        </div>
        <div>
          <strong>{{ stats.saved }}</strong>
          <span>{{ $t('components.tachoAnalysis.drivers.stats.saved') }}</span>
        </div>
        <div>
          <strong>{{ stats.missing }}</strong>
          <span>{{ $t('components.tachoAnalysis.drivers.stats.missing') }}</span>
        </div>
        <div>
          <strong>{{ stats.days }}</strong>
          <span>{{ $t('components.tachoAnalysis.drivers.stats.days') }}</span>
        </div>
      </div>

      <!-- Collapsible import panel -->
      <section
        v-if="uploadOpen"
        class="tfa-upload"
      >
        <div class="tfa-upload-head">
          <div>
            <h3>{{ $t('components.tachoAnalysis.drivers.importTitle') }}</h3>
            <p>{{ $t('components.tachoAnalysis.drivers.importSub') }}</p>
          </div>
          <div class="tfa-upload-options">
            <button
              type="button"
              class="tfa-btn"
              @click="closeUpload"
            >
              {{ $t('components.tachoAnalysis.drivers.close') }}
            </button>
          </div>
        </div>

        <div
          v-if="loading"
          class="tfa-loading-box"
        >
          <span class="spinner-border spinner-border-sm" /> {{ $t('components.tachoAnalysis.processing') }}
        </div>

        <div
          v-else
          class="tfa-dropzone"
          :class="{ 'tfa-dropzone--active': dragActive }"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
          @click="triggerFilePicker"
        >
          <i class="bi bi-cloud-arrow-up" />
          <p class="tfa-dropzone-title">
            {{ $t('components.tachoAnalysis.dropTitle') }}
          </p>
          <p class="tfa-dropzone-sub">
            {{ $t('components.tachoAnalysis.dropSub') }}
          </p>
          <span class="tfa-dropzone-btn"><i class="bi bi-folder2-open" /> {{ $t('components.tachoAnalysis.chooseFile') }}</span>
        </div>
      </section>

      <!-- Coverage / status filters -->
      <div class="tfa-coverage">
        <div class="tfa-coverage-label">
          <strong>{{ $t('components.tachoAnalysis.drivers.coverage') }}</strong>
          <span>{{ $t('components.tachoAnalysis.drivers.coverageSub') }}</span>
        </div>
        <div class="tfa-coverage-controls">
          <div class="tfa-quick">
            <button
              v-for="s in statusFilters"
              :key="s.key"
              type="button"
              :class="{ active: statusFilter === s.key }"
              @click="statusFilter = s.key"
            >
              {{ s.label }}
            </button>
          </div>
          <label>{{ $t('components.tachoAnalysis.from') }}</label>
          <input
            v-model="coverageFrom"
            type="date"
          >
          <label>{{ $t('components.tachoAnalysis.to') }}</label>
          <input
            v-model="coverageTo"
            type="date"
          >
          <button
            type="button"
            class="tfa-btn"
            :disabled="!coverageFrom && !coverageTo"
            @click="coverageFrom = ''; coverageTo = ''"
          >
            {{ $t('components.tachoAnalysis.drivers.clear') }}
          </button>
        </div>
      </div>

      <div
        v-if="driversError"
        class="tfa-error"
      >
        {{ driversError }}
      </div>

      <DxDataGrid
        ref="driversGrid"
        class="tfa-drivers-grid"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        :data-source="driversStore"
        :hover-state-enabled="true"
        :no-data-text="$t('components.tachoAnalysis.drivers.noDrivers')"
        :remote-operations="{ paging: true, sorting: true, filtering: true }"
        :row-alternation-enabled="true"
        :show-borders="false"
        :show-column-lines="false"
      >
        <DxFilterRow :visible="true" />
        <DxPaging :page-size="25" />
        <DxPager
          :show-info="true"
          :show-navigation-buttons="true"
          :visible="true"
          :info-text="$t('components.devexpress.pagerPage')
            + '{0}' + $t('components.devexpress.pagerOf')
            + '{1}'"
        />
        <DxColumn
          :caption="$t('components.tachoAnalysis.drivers.grid.driver')"
          :calculate-filter-expression="driverFilterExpression"
          :min-width="220"
          data-field="last_name"
          cell-template="driver-cell"
        />
        <DxColumn
          :caption="$t('components.tachoAnalysis.drivers.grid.card')"
          :min-width="180"
          data-field="card_number"
          cell-template="card-cell"
        />
        <DxColumn
          :allow-filtering="false"
          :allow-search="false"
          :caption="$t('components.tachoAnalysis.drivers.grid.range')"
          :min-width="210"
          data-field="activity_from"
          cell-template="range-cell"
        />
        <DxColumn
          :allow-filtering="false"
          :allow-search="false"
          :caption="$t('components.tachoAnalysis.drivers.grid.lastImport')"
          :min-width="170"
          data-field="last_uploaded_at"
          sort-order="desc"
          cell-template="import-cell"
        />
        <DxColumn
          :allow-filtering="false"
          :allow-search="false"
          :allow-sorting="false"
          :caption="$t('components.tachoAnalysis.drivers.grid.state')"
          css-class="tfa-grid-state-cell"
          :width="140"
          cell-template="state-cell"
        />
        <DxColumn
          :allow-filtering="false"
          :allow-search="false"
          :allow-sorting="false"
          :caption="$t('components.tachoAnalysis.drivers.grid.actions')"
          :width="220"
          cell-template="actions-cell"
        />

        <template #driver-cell="{ data }">
          <div class="tfa-grid-driver">
            <span class="tfa-grid-avatar">{{ driverInitials(data.data) }}</span>
            <div>
              <strong>{{ driverFullName(data.data) || $t('components.tachoAnalysis.drivers.unknown') }}</strong>
              <small>ID {{ data.data.driver_id }}</small>
            </div>
          </div>
        </template>
        <template #card-cell="{ data }">
          <div v-if="data.data.card_number">
            <strong class="mono">{{ data.data.card_number }}</strong>
            <small v-if="data.data.card_expiry_date">{{ $t('components.tachoAnalysis.drivers.validTo') }} {{ fmtDay(data.data.card_expiry_date) }}</small>
          </div>
          <span
            v-else
            class="tfa-grid-empty"
          >{{ $t('components.tachoAnalysis.drivers.noCard') }}</span>
        </template>
        <template #range-cell="{ data }">
          <div v-if="data.data.activity_from">
            <strong>{{ fmtDay(data.data.activity_from) }} – {{ fmtDay(data.data.activity_to) }}</strong>
            <small>{{ data.data.days_count }} {{ $t('components.tachoAnalysis.drivers.savedDays') }}</small>
          </div>
          <span
            v-else
            class="tfa-grid-empty"
          >{{ $t('components.tachoAnalysis.drivers.noData') }}</span>
        </template>
        <template #import-cell="{ data }">
          <div v-if="data.data.last_uploaded_at">
            <strong>{{ fmtDateTime(data.data.last_uploaded_at) }}</strong>
            <small v-if="data.data.last_filename">{{ data.data.last_filename }}</small>
          </div>
          <span
            v-else
            class="tfa-grid-empty"
          >{{ $t('components.tachoAnalysis.drivers.noImport') }}</span>
        </template>
        <template #state-cell="{ data }">
          <span
            class="tfa-pill"
            :class="data.data.has_saved_data ? 'tfa-pill--ready' : 'tfa-pill--missing'"
          >{{ data.data.has_saved_data ? $t('components.tachoAnalysis.drivers.stateReady') : $t('components.tachoAnalysis.drivers.stateMissing') }}</span>
        </template>
        <template #actions-cell="{ data }">
          <div class="tfa-grid-actions">
            <button
              v-if="data.data.has_saved_data"
              type="button"
              class="tfa-btn tfa-btn--primary tfa-btn--sm"
              @click="openDriver(data.data)"
            >
              {{ $t('components.tachoAnalysis.drivers.openData') }}
            </button>
            <button
              type="button"
              class="tfa-btn tfa-btn--sm"
              @click="openRemoteFiles(data.data)"
            >
              {{ $t('components.tachoAnalysis.remoteFiles.button') }}
            </button>
          </div>
        </template>
      </DxDataGrid>

      <TachoRemoteFilesModal
        ref="remoteFilesModal"
        @imported="refreshDrivers"
      />
    </section>

    <section
      v-if="detailOnly && loading"
      class="tfa-card tfa-detail-state"
    >
      <span class="spinner-border spinner-border-sm" /> {{ $t('components.tachoAnalysis.processing') }}
    </section>

    <section
      v-if="detailOnly && !loading && driversError"
      class="tfa-card tfa-detail-state tfa-detail-state--error"
    >
      <div class="tfa-error">
        {{ driversError }}
      </div>
      <button
        type="button"
        class="tfa-btn"
        @click="closeWorkspace"
      >
        <i class="bi bi-arrow-left" /> {{ $t('components.tachoAnalysis.drivers.backToList') }}
      </button>
    </section>

    <template v-if="result">
      <!-- Workspace context: what is on screen (uploaded file vs stored data) + actions -->
      <div class="tfa-options">
        <span
          v-if="source === 'stored'"
          class="tfa-file-chip"
        ><i class="bi bi-database-check" /> {{ $t('components.tachoAnalysis.drivers.storedChip') }}<template v-if="storedMeta?.last_uploaded_at"> · {{ $t('components.tachoAnalysis.drivers.grid.lastImport') }} {{ fmtDateTime(storedMeta.last_uploaded_at) }}</template></span>
        <span class="tfa-options-spacer" />
        <button
          type="button"
          class="tfa-btn"
          @click="closeWorkspace"
        >
          <i
            class="bi"
            :class="detailOnly ? 'bi-arrow-left' : 'bi-x-lg'"
          /> {{ detailOnly ? $t('components.tachoAnalysis.drivers.backToList') : $t('components.tachoAnalysis.drivers.close') }}
        </button>
      </div>

      <!-- Driver header -->
      <div class="tfa-driver">
        <div class="tfa-avatar">
          {{ holderInitials }}
        </div>
        <div class="tfa-driver-main">
          <div class="tfa-driver-name">
            {{ display(summary.holder_name) }}
          </div>
          <div class="tfa-driver-meta">
            <span
              v-for="(item, i) in driverMetaItems"
              :key="i"
              class="tfa-driver-meta-chip"
              :class="{ mono: item.mono }"
            >
              <i
                v-if="item.icon"
                class="bi"
                :class="item.icon"
              />{{ item.label }}
            </span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tfa-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
          <span
            v-if="tab.badge"
            class="tfa-badge"
          >{{ tab.badge }}</span>
        </button>
      </div>

      <!-- Activity -->
      <div
        v-if="activeTab === 'activity'"
        class="tfa-card"
        ref="activityCard"
      >
        <div class="tfa-card-head">
          <div class="tfa-card-head-left">
            <span class="tfa-card-title">{{ $t('components.tachoAnalysis.tabs.activity') }}</span>
            <div class="tfa-view-toggle">
              <button
                type="button"
                :class="{ active: viewMode === 'week' }"
                @click="setViewMode('week')"
              >
                {{ $t('components.tachoAnalysis.viewWeek') }}
              </button>
              <button
                type="button"
                :class="{ active: viewMode === 'day' }"
                @click="setViewMode('day')"
              >
                {{ $t('components.tachoAnalysis.viewDay') }}
              </button>
            </div>
          </div>
          <div class="tfa-legend">
            <span><span class="sq drive" />{{ $t('components.tachoAnalysis.activities.drive') }}</span>
            <span><span class="sq rest" />{{ $t('components.tachoAnalysis.activities.rest') }}</span>
            <span><span class="sq work" />{{ $t('components.tachoAnalysis.activities.work') }}</span>
            <span><span class="sq avail" />{{ $t('components.tachoAnalysis.activities.available') }}</span>
            <span><span class="tfa-place-legend tfa-place-legend--start">{{ $t('components.tachoAnalysis.placeShort.start') }}</span>{{ $t('components.tachoAnalysis.legendPlaceStart') }}</span>
            <span><span class="tfa-place-legend tfa-place-legend--end">{{ $t('components.tachoAnalysis.placeShort.end') }}</span>{{ $t('components.tachoAnalysis.legendPlaceEnd') }}</span>
            <span v-if="hasFerryConditions"><span class="tfa-ferry-legend">P</span>{{ $t('components.tachoAnalysis.ferry.legend') }}</span>
          </div>
        </div>

        <div
          v-if="!days.length"
          class="tfa-empty"
        >
          {{ $t('components.tachoAnalysis.noActivity') }}
        </div>
        <template v-else>
          <div class="tfa-range-filter">
            <div class="tfa-quick">
              <button
                v-for="p in presets"
                :key="p.key"
                type="button"
                :class="{ active: rangePreset === p.key }"
                @click="setPreset(p.key)"
              >
                {{ p.label }}
              </button>
            </div>
            <div class="tfa-range-custom">
              <label>{{ $t('components.tachoAnalysis.from') }}</label>
              <input
                type="date"
                :value="effectiveFrom"
                :min="minDate"
                :max="maxDate"
                @change="onCustomFrom($event)"
              >
              <label>{{ $t('components.tachoAnalysis.to') }}</label>
              <input
                type="date"
                :value="effectiveTo"
                :min="minDate"
                :max="maxDate"
                @change="onCustomTo($event)"
              >
            </div>
            <span class="tfa-range-count">{{ $t('components.tachoAnalysis.daysOf', { shown: filteredDays.length, total: days.length }) }}</span>
          </div>

          <div
            v-if="times"
            class="tfa-times"
          >
            <div class="tfa-times-head">
              <span>{{ $t('components.tachoAnalysis.times.title') }}</span>
              <small>{{ $t('components.tachoAnalysis.times.reference') }} {{ fmtDateTime(times.reference) }}</small>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.dayDriving') }}</small>
              <strong>{{ times.day.driving_min === null ? '—' : hhmm(times.day.driving_min) }}</strong>
              <span v-if="times.day.rest_end">{{ $t('components.tachoAnalysis.times.sinceRest') }} {{ fmtDateTime(times.day.rest_end) }}</span>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.dayWork') }}</small>
              <strong>{{ times.day.work_min === null ? '—' : hhmm(times.day.work_min) }}</strong>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.continuous') }}</small>
              <strong>{{ hhmm(times.continuous.driving_min) }}</strong>
              <span>{{ $t('components.tachoAnalysis.times.remainingBreak') }} {{ hhmm(times.continuous.remaining_min) }}</span>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.weekDriving') }}</small>
              <strong>{{ hhmm(times.weeks.current_driving_min) }}</strong>
              <span>{{ $t('components.tachoAnalysis.times.twoWeeks') }} {{ hhmm(times.weeks.two_weeks_driving_min) }} / 90:00</span>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.cycle') }}</small>
              <strong>{{ times.cycle.extended_daily_drives }} / 2 · {{ times.cycle.workdays }} / 6 · {{ times.cycle.short_daily_rests }} / 3</strong>
              <span>{{ $t('components.tachoAnalysis.times.cycleHint') }}</span>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.lastDailyRest') }}</small>
              <strong>{{ times.last_daily_rest ? hhmm(times.last_daily_rest.duration_min) : '—' }}</strong>
              <span v-if="times.last_daily_rest">{{ times.last_daily_rest.label }}</span>
            </div>
            <div class="tfa-times-tile">
              <small>{{ $t('components.tachoAnalysis.times.lastWeeklyRest') }}</small>
              <strong>{{ times.last_weekly_rest ? hhmm(times.last_weekly_rest.duration_min) : '—' }}</strong>
              <span v-if="times.last_weekly_rest">{{ times.last_weekly_rest.label }}</span>
            </div>
          </div>

          <div
            v-if="!filteredDays.length"
            class="tfa-empty"
          >
            {{ $t('components.tachoAnalysis.noDaysInRange') }}
          </div>
          <template v-if="viewMode === 'day'">
            <div class="tfa-day-workspace">
              <aside class="tfa-day-rail">
                <div class="tfa-rail-summary">
                  <div class="tfa-rail-summary-title">
                    {{ $t('components.tachoAnalysis.rangeSummary', { days: filteredDays.length }) }}
                  </div>
                  <div class="tfa-rail-summary-grid">
                    <span><span class="tfa-sq tfa-sq--drive" />{{ hhmm(rangeTotals.drive) }}</span>
                    <span><span class="tfa-sq tfa-sq--work" />{{ hhmm(rangeTotals.work) }}</span>
                    <span><span class="tfa-sq tfa-sq--avail" />{{ hhmm(rangeTotals.available) }}</span>
                    <span><span class="tfa-sq tfa-sq--rest" />{{ hhmm(rangeTotals.rest) }}</span>
                  </div>
                </div>

                <button
                  v-for="day in filteredDays"
                  :key="day.date"
                  type="button"
                  class="tfa-rail-day"
                  :class="{ 'is-active': selectedDay?.date === day.date }"
                  @click="selectDay(day.date)"
                >
                  <span class="tfa-rail-date">
                    {{ fmtDay(day.date) }}
                    <small>{{ dowAbbr(momentDayNumber(day.date)) }}</small>
                  </span>
                  <span class="tfa-rail-drive">
                    {{ hhmm(day.totals?.drive || 0) }}
                    <small>{{ $t('components.tachoAnalysis.activities.drive') }}</small>
                  </span>
                  <span class="tfa-mini-track">
                    <span
                      v-for="(bar, i) in miniDayBars(day)"
                      :key="i"
                      class="tfa-mini-seg"
                      :class="['tfa-mini-seg--' + bar.kind, bar.restKind ? 'tfa-rest--' + bar.restKind : '']"
                      :style="{ left: bar.left, width: bar.width }"
                    />
                    <span
                      v-for="(range, i) in miniDayFerry(day).ranges"
                      :key="'fr' + i"
                      class="tfa-mini-ferry-range"
                      :style="{ left: range.left, width: range.width }"
                    >
                      <span class="tfa-mini-ferry-label">P</span>
                      <span class="tfa-activity-tooltip">
                        <span class="tfa-activity-tooltip-head">
                          <span class="tfa-activity-tooltip-dot" />
                          <b>{{ range.tooltip.label }}</b>
                        </span>
                        <span class="tfa-activity-tooltip-rest">{{ range.tooltip.detail }}</span>
                        <span class="tfa-activity-tooltip-range">{{ range.tooltip.range }}</span>
                        <span class="tfa-activity-tooltip-duration">{{ range.tooltip.duration }}</span>
                        <span class="tfa-activity-tooltip-day">{{ range.tooltip.day }}</span>
                      </span>
                    </span>
                    <span
                      v-for="(marker, i) in miniDayFerry(day).markers"
                      :key="'fm' + i"
                      class="tfa-mini-ferry-marker"
                      :style="{ left: marker.left }"
                    >
                      P
                      <span class="tfa-activity-tooltip">
                        <span class="tfa-activity-tooltip-head">
                          <span class="tfa-activity-tooltip-dot" />
                          <b>{{ marker.tooltip.label }}</b>
                        </span>
                        <span class="tfa-activity-tooltip-rest">{{ marker.tooltip.detail }}</span>
                        <span class="tfa-activity-tooltip-range">{{ marker.tooltip.range }}</span>
                        <span class="tfa-activity-tooltip-duration">{{ marker.tooltip.duration }}</span>
                        <span class="tfa-activity-tooltip-day">{{ marker.tooltip.day }}</span>
                      </span>
                    </span>
                    <span
                      v-for="(place, i) in miniDayPlaces(day)"
                      :key="'p' + i"
                      class="tfa-mini-place"
                      :class="'tfa-mini-place--' + place.kind"
                      :style="{ left: place.left }"
                      :title="place.title"
                    >
                      {{ place.label }}
                    </span>
                  </span>
                </button>
              </aside>

              <section
                v-if="selectedDay"
                class="tfa-day-pane"
              >
                <div class="tfa-day-pane-head">
                  <button
                    type="button"
                    class="tfa-day-nav"
                    :disabled="!canMoveSelectedDay(-1)"
                    :title="$t('components.tachoAnalysis.prevDay')"
                    @click="moveSelectedDay(-1)"
                  >
                    <i class="bi bi-chevron-left" />
                  </button>
                  <div class="tfa-day-pane-title">
                    <h2>{{ dowName(selectedDay.date) }}, {{ fmtDay(selectedDay.date) }}</h2>
                    <small>{{ $t('components.tachoAnalysis.dayOfRange', { current: selectedDayIndex + 1, total: filteredDays.length }) }}</small>
                  </div>
                  <button
                    type="button"
                    class="tfa-day-nav"
                    :disabled="!canMoveSelectedDay(1)"
                    :title="$t('components.tachoAnalysis.nextDay')"
                    @click="moveSelectedDay(1)"
                  >
                    <i class="bi bi-chevron-right" />
                  </button>
                </div>

                <div class="tfa-chipstats">
                  <span
                    v-for="item in selectedDayTotals"
                    :key="item.key"
                    class="tfa-chipstat"
                  >
                    <span
                      class="tfa-sq"
                      :class="'tfa-sq--' + item.kind"
                    />{{ item.label }} <b>{{ item.value }}</b>
                  </span>
                  <span
                    v-if="selectedDay.day_distance_km != null"
                    class="tfa-chipstat"
                  >
                    {{ $t('components.tachoAnalysis.distance') }} <b>{{ group(selectedDay.day_distance_km) }} km</b>
                  </span>
                </div>

                <div class="tfa-day-section">
                  <div class="tfa-chart">
                    <div class="tfa-gutter">
                      <span>{{ $t('components.tachoAnalysis.gutter.drive') }}</span><span>{{ $t('components.tachoAnalysis.gutter.rest') }}</span><span>{{ $t('components.tachoAnalysis.gutter.work') }}</span>
                    </div>
                    <div class="tfa-track">
                      <div
                        v-for="(bar, i) in dayBarsZoom(selectedDay)"
                        :key="'b' + i"
                        class="tfa-bar"
                        :class="['tfa-bar--' + bar.kind, bar.restKind ? 'tfa-rest--' + bar.restKind : '']"
                        :style="{ left: bar.left, width: bar.width }"
                      >
                        <span
                          v-if="bar.showLabel"
                          class="tfa-bar-label"
                        >{{ bar.label }}</span>
                        <span class="tfa-activity-tooltip">
                          <span class="tfa-activity-tooltip-head">
                            <span class="tfa-activity-tooltip-dot" />
                            <b>{{ bar.tooltip.label }}</b>
                          </span>
                          <span
                            v-if="bar.restLabel"
                            class="tfa-activity-tooltip-rest"
                          >{{ bar.restLabel }}</span>
                          <span class="tfa-activity-tooltip-range">{{ bar.tooltip.range }}</span>
                          <span class="tfa-activity-tooltip-duration">{{ bar.tooltip.duration }}</span>
                          <span class="tfa-activity-tooltip-day">{{ bar.tooltip.day }}</span>
                        </span>
                      </div>
                      <span
                        v-for="(range, i) in dayFerryZoom(selectedDay).ranges"
                        :key="'fr' + i"
                        class="tfa-ferry-range"
                        :style="{ left: range.left, width: range.width }"
                      >
                        <span class="tfa-ferry-range-label">P</span>
                        <span class="tfa-activity-tooltip">
                          <span class="tfa-activity-tooltip-head">
                            <span class="tfa-activity-tooltip-dot" />
                            <b>{{ range.tooltip.label }}</b>
                          </span>
                          <span class="tfa-activity-tooltip-rest">{{ range.tooltip.detail }}</span>
                          <span class="tfa-activity-tooltip-range">{{ range.tooltip.range }}</span>
                          <span class="tfa-activity-tooltip-duration">{{ range.tooltip.duration }}</span>
                          <span class="tfa-activity-tooltip-day">{{ range.tooltip.day }}</span>
                        </span>
                      </span>
                      <span
                        v-for="(marker, i) in dayFerryZoom(selectedDay).markers"
                        :key="'fm' + i"
                        class="tfa-ferry-marker"
                        :style="{ left: marker.left }"
                      >
                        P
                        <span class="tfa-activity-tooltip">
                          <span class="tfa-activity-tooltip-head">
                            <span class="tfa-activity-tooltip-dot" />
                            <b>{{ marker.tooltip.label }}</b>
                          </span>
                          <span class="tfa-activity-tooltip-rest">{{ marker.tooltip.detail }}</span>
                          <span class="tfa-activity-tooltip-range">{{ marker.tooltip.range }}</span>
                          <span class="tfa-activity-tooltip-duration">{{ marker.tooltip.duration }}</span>
                          <span class="tfa-activity-tooltip-day">{{ marker.tooltip.day }}</span>
                        </span>
                      </span>
                      <div
                        v-for="(line, i) in dayPlaceLinesZoom(selectedDay)"
                        :key="'l' + i"
                        class="tfa-place-line"
                        :class="'tfa-place-line--' + line.kind"
                        :style="{ left: line.left }"
                        :title="line.tooltip.title"
                      >
                        <span class="tfa-place-marker-label">{{ line.label }}</span>
                        <span class="tfa-place-tooltip">
                          <span class="tfa-place-tooltip-head">
                            <span class="tfa-place-tooltip-dot" />
                            <b>{{ line.tooltip.type }}</b>
                          </span>
                          <span class="tfa-place-tooltip-time">{{ line.tooltip.time }}</span>
                          <span class="tfa-place-tooltip-location">{{ line.tooltip.location }}</span>
                          <span class="tfa-place-tooltip-odo">{{ line.tooltip.odo }}</span>
                          <span class="tfa-place-tooltip-day">{{ line.tooltip.day }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="tfa-axis">
                    <span
                      v-for="(t, i) in dayAxis(selectedDay)"
                      :key="i"
                    >{{ t.label }}</span>
                  </div>
                  <div
                    v-if="selectedDayPlaces.length"
                    class="tfa-place-chips"
                  >
                    <span
                      v-for="(p, i) in selectedDayPlaces"
                      :key="'pc' + i"
                      class="tfa-place-chip"
                      :class="'tfa-place-chip--' + placeMarkerKind(p)"
                      :title="placeTooltip(p, dowName(selectedDay.date) + ' ' + selectedDay.date).title"
                    >
                      <b>{{ placeShortLabel(p) }} {{ p._time || fmtTime(p.Timestamp) }}</b>
                      {{ placeTypeLabel(p.EntryType) }}
                    </span>
                  </div>

                  <div
                    v-if="canZoomOutDay(selectedDay.date)"
                    class="tfa-brush"
                  >
                    <div
                      v-for="(bar, i) in brushBars(selectedDay)"
                      :key="'bb' + i"
                      class="tfa-brush-bar"
                      :class="['tfa-brush-bar--' + bar.kind, bar.restKind ? 'tfa-rest--' + bar.restKind : '']"
                      :style="{ left: bar.left, width: bar.width }"
                    />
                    <span
                      v-for="(range, i) in miniDayFerry(selectedDay).ranges"
                      :key="'bfr' + i"
                      class="tfa-brush-ferry-range"
                      :style="{ left: range.left, width: range.width }"
                    />
                    <span
                      v-for="(marker, i) in miniDayFerry(selectedDay).markers"
                      :key="'bfm' + i"
                      class="tfa-brush-ferry-marker"
                      :style="{ left: marker.left }"
                    >P</span>
                    <div
                      class="tfa-brush-mask"
                      :style="brushMaskLeft(selectedDay.date)"
                    />
                    <div
                      class="tfa-brush-mask"
                      :style="brushMaskRight(selectedDay.date)"
                    />
                    <div
                      class="tfa-brush-sel"
                      :style="brushSel(selectedDay.date)"
                      @mousedown="onBrushDown('pan', selectedDay.date, $event)"
                    >
                      <span
                        class="tfa-brush-handle left"
                        @mousedown.stop="onBrushDown('left', selectedDay.date, $event)"
                      />
                      <span
                        class="tfa-brush-handle right"
                        @mousedown.stop="onBrushDown('right', selectedDay.date, $event)"
                      />
                    </div>
                  </div>
                </div>

                <div class="tfa-day-section">
                  <div class="tfa-detail tfa-detail--day-pane">
                    <button
                      type="button"
                      class="tfa-detail-toggle"
                      @click="toggleDetail(selectedDay.date)"
                    >
                      <i
                        class="bi"
                        :class="isDetailOpen(selectedDay.date) ? 'bi-chevron-down' : 'bi-chevron-right'"
                      />
                      {{ $t('components.tachoAnalysis.detailList') }}
                      <span class="tfa-detail-count">{{ segmentRows(selectedDay).length }}</span>
                    </button>
                    <div
                      v-if="isDetailOpen(selectedDay.date)"
                      class="tfa-ivs"
                    >
                      <div
                        v-for="(s, i) in segmentRows(selectedDay)"
                        :key="'s' + i"
                        class="tfa-iv"
                      >
                        <span
                          class="tfa-iv-bar"
                          :class="['tfa-iv-bar--' + s.kind, s.restKind ? 'tfa-rest--' + s.restKind : '']"
                        />
                        <span class="tfa-iv-time mono">{{ s.start }} – {{ s.stop }}</span>
                        <span class="tfa-iv-name">{{ s.label }}</span>
                        <span
                          v-if="s.hasPlace || s.ferry"
                          class="tfa-iv-tags"
                        >
                          <span
                            v-if="s.hasPlace"
                            class="tfa-place-tag"
                          ><span class="tfa-place-mark-sm" />{{ $t('components.tachoAnalysis.placeTag') }}</span>
                          <span
                            v-if="s.ferry"
                            class="tfa-ferry-tag"
                          ><span class="tfa-ferry-mark-sm">P</span>{{ s.ferry }}</span>
                        </span>
                        <span class="tfa-iv-dur mono">{{ hhmm(s.duration_min) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="selectedDayVehicles.length"
                  class="tfa-day-section"
                >
                  <h3>{{ $t('components.tachoAnalysis.vehicleInDay') }}</h3>
                  <div class="tfa-day-rows">
                    <div
                      v-for="(v, i) in selectedDayVehicles"
                      :key="'v' + i"
                      class="tfa-day-row"
                    >
                      <i class="bi bi-truck" />
                      <div>
                        <strong>{{ v.RegistrationNumber || '—' }}</strong>
                        <small>{{ fmtDateTime(v.FirstUse) }} → {{ fmtDateTime(v.LastUse) }} · {{ $t('components.tachoAnalysis.odometer') }} {{ odometerLabel(v.OdometerBegin) }} → {{ odometerLabel(v.OdometerEnd) }}</small>
                      </div>
                      <b v-if="vehicleDistanceLabel(v)">{{ vehicleDistanceLabel(v) }}</b>
                    </div>
                  </div>
                </div>

                <div
                  v-if="selectedDayPlaces.length"
                  class="tfa-day-section"
                >
                  <h3>{{ $t('components.tachoAnalysis.placesInDay') }}</h3>
                  <div class="tfa-day-rows">
                    <div
                      v-for="(p, i) in selectedDayPlaces"
                      :key="'p' + i"
                      class="tfa-day-row"
                    >
                      <i class="bi bi-geo-alt" />
                      <div>
                        <strong>{{ placeTypeLabel(p.EntryType) }} · {{ p._time || fmtTime(p.Timestamp) }}</strong>
                        <small>{{ placeLocation(p) }} · {{ $t('components.tachoAnalysis.odometer') }} {{ p.Odometer != null ? group(p.Odometer) + ' km' : '—' }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="selectedDayEvents.length"
                  class="tfa-day-section"
                >
                  <h3>{{ $t('components.tachoAnalysis.eventsInDay') }}</h3>
                  <div class="tfa-day-rows">
                    <div
                      v-for="(e, i) in selectedDayEvents"
                      :key="'e' + i"
                      class="tfa-day-row"
                    >
                      <i class="bi bi-exclamation-triangle" />
                      <div>
                        <strong>{{ e.title }}</strong>
                        <small>{{ e.date }} {{ e.time }} · {{ e.duration || '—' }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section
                v-else
                class="tfa-day-pane"
              >
                <div class="tfa-empty tfa-empty--pad">
                  {{ $t('components.tachoAnalysis.noDaySelected') }}
                </div>
              </section>
            </div>
          </template>

          <template v-else>
            <template
              v-for="grp in displayGroups"
              :key="grp.key"
            >
              <!-- Week mode: header + one combined Mon–Sun chart -->
              <template v-if="grp.week">
                <div class="tfa-week-head">
                  <div class="tfa-week-title">
                    {{ $t('components.tachoAnalysis.week') }} {{ grp.week.week }} <span>({{ grp.week.from }} – {{ grp.week.to }})</span>
                  </div>
                  <div class="tfa-week-sums">
                    <span class="tfa-week-sum drive">{{ $t('components.tachoAnalysis.activities.drive') }} {{ hhmm(grp.week.totals.drive) }}</span>
                    <span class="tfa-week-sum work">{{ $t('components.tachoAnalysis.activities.work') }} {{ hhmm(grp.week.totals.work) }}</span>
                    <span class="tfa-week-sum dw">{{ $t('components.tachoAnalysis.driveWork') }} {{ hhmm(grp.week.totals.drive + grp.week.totals.work) }}</span>
                  </div>
                </div>
                <div class="tfa-chart tfa-chart--week">
                  <div class="tfa-gutter">
                    <span>{{ $t('components.tachoAnalysis.gutter.drive') }}</span><span>{{ $t('components.tachoAnalysis.gutter.rest') }}</span><span>{{ $t('components.tachoAnalysis.gutter.work') }}</span>
                  </div>
                  <div class="tfa-track">
                    <div
                      v-for="col in weekColumns(grp.week, grp.days)"
                      :key="'sep' + col.date"
                      v-show="col.index > 0"
                      class="tfa-day-sep"
                      :style="{ left: col.left }"
                    />
                    <div
                      v-for="col in weekColumns(grp.week, grp.days)"
                      :key="'col' + col.date"
                      class="tfa-day-col"
                      :class="{ present: col.present, 'is-hovered': hoveredWeekDate === col.date }"
                      :style="{ left: col.left, width: col.width }"
                      @mouseenter="col.present && setWeekHover(col.date)"
                      @mouseleave="clearWeekHover(col.date)"
                      @click="col.present && goToDay(col.date)"
                    >
                      <span
                        v-if="col.present"
                        class="tfa-day-tooltip"
                      >
                        <span class="tfa-day-tooltip-head">
                          <span class="tfa-day-tooltip-dot" />
                          <b>{{ col.tooltip.date }}</b>
                        </span>
                        <span class="tfa-day-tooltip-dow">{{ col.tooltip.dow }}</span>
                        <span class="tfa-day-tooltip-total drive">{{ $t('components.tachoAnalysis.activities.drive') }} {{ col.tooltip.drive }}</span>
                        <span class="tfa-day-tooltip-total work">{{ $t('components.tachoAnalysis.activities.work') }} {{ col.tooltip.work }}</span>
                        <span class="tfa-day-tooltip-total">{{ $t('components.tachoAnalysis.driveWork') }} {{ col.tooltip.driveWork }}</span>
                      </span>
                    </div>
                    <div
                      v-for="(bar, i) in weekBars(grp.week, grp.days)"
                      :key="'wb' + i"
                      class="tfa-bar"
                      :class="['tfa-bar--' + bar.kind, bar.restKind ? 'tfa-rest--' + bar.restKind : '']"
                      :style="{ left: bar.left, width: bar.width }"
                      @mouseenter="setWeekHover(bar.date)"
                      @mouseleave="clearWeekHover(bar.date)"
                      @click.stop="goToDay(bar.date)"
                    >
                      <span
                        v-if="bar.showLabel"
                        class="tfa-bar-label"
                      >{{ bar.label }}</span>
                      <span class="tfa-activity-tooltip">
                        <span class="tfa-activity-tooltip-head">
                          <span class="tfa-activity-tooltip-dot" />
                          <b>{{ bar.tooltip.label }}</b>
                        </span>
                        <span
                          v-if="bar.restLabel"
                          class="tfa-activity-tooltip-rest"
                        >{{ bar.restLabel }}</span>
                        <span class="tfa-activity-tooltip-range">{{ bar.tooltip.range }}</span>
                        <span class="tfa-activity-tooltip-duration">{{ bar.tooltip.duration }}</span>
                        <span class="tfa-activity-tooltip-day">{{ bar.tooltip.day }}</span>
                      </span>
                    </div>
                    <span
                      v-for="(range, i) in weekFerry(grp.week, grp.days).ranges"
                      :key="'wfr' + i"
                      class="tfa-ferry-range tfa-ferry-range--week"
                      :style="{ left: range.left, width: range.width }"
                      @mouseenter="setWeekHover(range.date)"
                      @mouseleave="clearWeekHover(range.date)"
                      @click.stop="goToDay(range.date)"
                    >
                      <span class="tfa-ferry-range-label">P</span>
                      <span class="tfa-activity-tooltip">
                        <span class="tfa-activity-tooltip-head">
                          <span class="tfa-activity-tooltip-dot" />
                          <b>{{ range.tooltip.label }}</b>
                        </span>
                        <span class="tfa-activity-tooltip-rest">{{ range.tooltip.detail }}</span>
                        <span class="tfa-activity-tooltip-range">{{ range.tooltip.range }}</span>
                        <span class="tfa-activity-tooltip-duration">{{ range.tooltip.duration }}</span>
                        <span class="tfa-activity-tooltip-day">{{ range.tooltip.day }}</span>
                      </span>
                    </span>
                    <span
                      v-for="(marker, i) in weekFerry(grp.week, grp.days).markers"
                      :key="'wfm' + i"
                      class="tfa-ferry-marker tfa-ferry-marker--week"
                      :style="{ left: marker.left }"
                      @mouseenter="setWeekHover(marker.date)"
                      @mouseleave="clearWeekHover(marker.date)"
                      @click.stop="goToDay(marker.date)"
                    >
                      P
                      <span class="tfa-activity-tooltip">
                        <span class="tfa-activity-tooltip-head">
                          <span class="tfa-activity-tooltip-dot" />
                          <b>{{ marker.tooltip.label }}</b>
                        </span>
                        <span class="tfa-activity-tooltip-rest">{{ marker.tooltip.detail }}</span>
                        <span class="tfa-activity-tooltip-range">{{ marker.tooltip.range }}</span>
                        <span class="tfa-activity-tooltip-duration">{{ marker.tooltip.duration }}</span>
                        <span class="tfa-activity-tooltip-day">{{ marker.tooltip.day }}</span>
                      </span>
                    </span>
                    <div
                      v-for="(line, i) in weekPlaceLines(grp.week, grp.days)"
                      :key="'wl' + i"
                      class="tfa-place-line"
                      :class="'tfa-place-line--' + line.kind"
                      :style="{ left: line.left }"
                      :title="line.tooltip.title"
                      @mouseenter="setWeekHover(line.date)"
                      @mouseleave="clearWeekHover(line.date)"
                    >
                      <span class="tfa-place-marker-label">{{ line.label }}</span>
                      <span class="tfa-place-tooltip">
                        <span class="tfa-place-tooltip-head">
                          <span class="tfa-place-tooltip-dot" />
                          <b>{{ line.tooltip.type }}</b>
                        </span>
                        <span class="tfa-place-tooltip-time">{{ line.tooltip.time }}</span>
                        <span class="tfa-place-tooltip-location">{{ line.tooltip.location }}</span>
                        <span class="tfa-place-tooltip-odo">{{ line.tooltip.odo }}</span>
                        <span class="tfa-place-tooltip-day">{{ line.tooltip.day }}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="tfa-week-axis">
                  <span
                    v-for="col in weekColumns(grp.week, grp.days)"
                    :key="'ax' + col.date"
                    :class="{ absent: !col.present, clickable: col.present }"
                    @click="col.present && goToDay(col.date)"
                  >{{ col.dow }} {{ col.dom }}</span>
                </div>
              </template>
            </template>
          </template>
        </template>
      </div>

      <!-- Vehicles -->
      <div
        v-else-if="activeTab === 'vehicles'"
        class="tfa-card tfa-card--table"
      >
        <div class="tfa-table-head">
          <div class="tfa-card-title">
            {{ $t('components.tachoAnalysis.vehiclesTitle') }}
          </div>
          <div class="tfa-table-meta">
            {{ vehicleSessions.length }} {{ $t('components.tachoAnalysis.sessionsUnit') }} · {{ (result.vehicles || []).length }} {{ $t('components.tachoAnalysis.daysUnit') }} · {{ $t('components.tachoAnalysis.total') }} <b>{{ group(kmSum) }} km</b>
          </div>
        </div>
        <div
          v-if="!vehicleSessions.length"
          class="tfa-empty tfa-empty--pad"
        >
          {{ $t('components.tachoAnalysis.noVehicles') }}
        </div>
        <div
          v-else
          class="tfa-veh-list"
        >
          <div
            v-for="(s, i) in vehicleSessions"
            :key="i"
            class="tfa-veh-card"
          >
            <div class="tfa-veh-head">
              <span class="tfa-plate">
                <span class="tfa-plate-cc">{{ s.country || '—' }}</span>
                <span class="tfa-plate-num">{{ s.vrn }}</span>
              </span>
              <span class="tfa-veh-range">
                {{ s.dateFrom }} – {{ s.dateTo }} · {{ s.days }} {{ $t('components.tachoAnalysis.daysUnit') }}
                <span
                  v-if="s.open"
                  class="tfa-veh-open"
                >{{ $t('components.tachoAnalysis.inUse') }}</span>
              </span>
            </div>
            <div class="tfa-spark-row">
              <span class="tfa-spark-cap">{{ $t('components.tachoAnalysis.kmPerDay') }}</span>
              <div class="tfa-spark">
                <span
                  v-for="(b, j) in s.bars"
                  :key="j"
                  class="tfa-spark-bar"
                  :style="{ height: b.h + '%' }"
                >
                  <span class="tfa-spark-tooltip">
                    <span class="tfa-spark-tooltip-head">
                      <span class="tfa-spark-tooltip-dot" />
                      <b>{{ $t('components.tachoAnalysis.dailyMileage') }}</b>
                    </span>
                    <span class="tfa-spark-tooltip-date">{{ b.date }}</span>
                    <span class="tfa-spark-tooltip-km">{{ group(b.km) }} km</span>
                  </span>
                </span>
              </div>
            </div>
            <div class="tfa-veh-foot">
              <span class="mono">{{ $t('components.tachoAnalysis.odometer') }} {{ s.odoBegin }} → {{ s.odoEnd }}</span>
              <span class="tfa-veh-total">Σ {{ s.totalKm }} km</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Places -->
      <div
        v-else-if="activeTab === 'places'"
        class="tfa-card tfa-card--table"
      >
        <div class="tfa-table-head">
          <div>
            <div class="tfa-card-title">
              {{ $t('components.tachoAnalysis.placesTitle') }}
            </div>
            <div class="tfa-table-sub">
              {{ $t('components.tachoAnalysis.placesSub') }}
            </div>
          </div>
        </div>
        <div
          v-if="!placesRows.length"
          class="tfa-empty tfa-empty--pad"
        >
          {{ $t('components.tachoAnalysis.noPlaces') }}
        </div>
        <template v-else>
          <div class="tfa-grid tfa-grid--places tfa-grid-head">
            <div>{{ $t('components.tachoAnalysis.placesCols.datetime') }}</div><div>{{ $t('components.tachoAnalysis.placesCols.type') }}</div><div>{{ $t('components.tachoAnalysis.placesCols.location') }}</div><div class="ar">
              {{ $t('components.tachoAnalysis.placesCols.odometer') }}
            </div>
          </div>
          <div
            v-for="(p, i) in placesRows"
            :key="i"
            class="tfa-grid tfa-grid--places tfa-grid-row"
          >
            <div class="mono tfa-cell-sub2">
              {{ p.datetime }}
            </div>
            <div class="tfa-cell-place">
              <span class="tfa-place-mark" />{{ p.type }}
            </div>
            <div>
              <a
                v-if="p.mapsUrl"
                :href="p.mapsUrl"
                target="_blank"
                rel="noopener"
              >
                {{ p.location }}
              </a>
              <span v-else>{{ p.location }}</span>
            </div>
            <div class="ar mono">
              {{ p.odo }}
            </div>
          </div>
        </template>
      </div>

      <!-- Events -->
      <div
        v-else-if="activeTab === 'events'"
        class="tfa-card tfa-card--table"
      >
        <div class="tfa-table-head">
          <div class="tfa-card-title">
            {{ $t('components.tachoAnalysis.eventsTitle') }}
          </div>
          <div class="tfa-table-meta">
            {{ eventsAll.length }} {{ $t('components.tachoAnalysis.itemsUnit') }}
          </div>
        </div>
        <div
          v-if="!eventsAll.length"
          class="tfa-empty tfa-empty--pad"
        >
          {{ $t('components.tachoAnalysis.noEvents') }}
        </div>
        <div v-else>
          <div
            v-for="(e, i) in eventsAll"
            :key="i"
            class="tfa-grid tfa-grid--events tfa-grid-row"
          >
            <div class="mono tfa-cell-sub2">
              {{ e.date }} {{ e.time }}
            </div>
            <div class="tfa-cell-evt">
              <span
                class="tfa-dot"
                :class="'tfa-dot--' + e.severity"
              />
              <div>
                <div class="fw6">
                  {{ e.title }}
                </div>
                <div class="tfa-cell-sub">
                  {{ e.detail || '—' }}
                </div>
              </div>
            </div>
            <div class="mono tfa-cell-sub2">
              {{ e.duration }}
            </div>
          </div>
        </div>
      </div>

      <!-- Card-specific conditions. The legacy analysis exposed these in a dedicated tab;
           ferry/train entries also remain visible on the activity timeline. -->
      <div
        v-else-if="activeTab === 'conditions'"
        class="tfa-card tfa-card--table"
      >
        <div class="tfa-table-head">
          <div>
            <div class="tfa-card-title">
              {{ $t('components.tachoAnalysis.conditionsTitle') }}
            </div>
            <div class="tfa-table-sub">
              {{ $t('components.tachoAnalysis.conditionsSub') }}
            </div>
          </div>
          <div class="tfa-table-meta">
            {{ conditionsAll.length }} {{ $t('components.tachoAnalysis.itemsUnit') }}
          </div>
        </div>
        <div
          v-if="!conditionsAll.length"
          class="tfa-empty tfa-empty--pad"
        >
          {{ $t('components.tachoAnalysis.noConditions') }}
        </div>
        <div v-else>
          <div
            v-for="(condition, i) in conditionsAll"
            :key="i"
            class="tfa-grid tfa-grid--events tfa-grid-row"
          >
            <div class="mono tfa-cell-sub2">
              {{ fmtDateTime(condition.Timestamp) }}
            </div>
            <div class="tfa-cell-evt">
              <span class="tfa-dot tfa-dot--info" />
              <div>
                <div class="fw6">
                  {{ conditionKindLabel(condition.Condition) }}
                </div>
                <div class="tfa-cell-sub">
                  {{ conditionPhaseLabel(condition.Phase) }}
                </div>
              </div>
            </div>
            <div class="mono tfa-cell-sub2">
              {{ $t('components.tachoAnalysis.conditionGeneration', { generation: condition.Generation }) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Card data -->
      <div
        v-else-if="activeTab === 'card'"
        class="tfa-card"
      >
        <div class="tfa-card-title tfa-mb">
          {{ $t('components.tachoAnalysis.cardTitle') }}
        </div>
        <div
          v-if="!cardRows.length"
          class="tfa-empty"
        >
          {{ $t('components.tachoAnalysis.noCardData') }}
        </div>
        <div
          v-else
          class="tfa-id-grid"
        >
          <div
            v-for="(row, i) in cardRows"
            :key="i"
          >
            <div class="tfa-id-label">
              {{ row.label }}
            </div>
            <div
              class="tfa-id-value"
              :class="{ mono: row.mono }"
            >
              {{ row.value }}
            </div>
          </div>
          <div v-if="cardStatus">
            <div class="tfa-id-label">
              {{ $t('components.tachoAnalysis.card.status') }}
            </div>
            <div class="tfa-id-value">
              <span
                class="tfa-status"
                :class="'tfa-status--' + cardStatus.kind"
              >
                <span class="tfa-status-dot" />{{ cardStatus.label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import moment from 'moment'
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { axiosInstance } from '@/http-common'
import { getReverseGeocode } from '@/crud/getters'
import { createDriversStore } from '@/stores/tachoActivityDriversStore'
import TachoRemoteFilesModal from '@/components/content/_modals/TachoRemoteFilesModal.vue'
import {
  hoursMinutes, clock, groupThousands, formatTime, formatDateFull,
  formatDateTime, durationBetween, localMinuteOfDay,
} from '@/utils/tacho/format'
import {
  activityKind, eventSeverity, presetRangeStart, alignPlacesToDay, groupDaysByIsoWeek,
  vehicleSessions, barsInWindow,
} from '@/utils/tacho/activity'

const WEEK_MIN = 7 * 1440 // minutes in a Mon–Sun week span
const DAY_MIN = 1440 // minutes in a day (full chart span)
const MIN_ZOOM_WINDOW = 60 // smallest zoom window in minutes

const DEFAULT_PRESET = { day: '28', week: '1mo' }
const PLACE_GEOCODE_CACHE_KEY = 'tacho.placeGeocodeCache.v1'
const PLACE_GEOCODE_MAX_CACHE = 750
const placeGeocodeMemory = new Map<string, string>()

export default defineComponent({
  name: 'TachoFileAnalysis',
  components: { DxColumn, DxDataGrid, DxFilterRow, DxPager, DxPaging, TachoRemoteFilesModal },
  props: {
    detailOnly: {
      type: Boolean,
      default: false,
    },
    driverId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      file: null as File | null,
      dragActive: false,
      loading: false,
      result: null as any,
      ingest: null as any, // "ingest" block of the save response; null until the file is saved
      source: null as null | 'stored', // what the workspace shows: stored DB data
      storedMeta: null as any, // "stored" block of the driver-data response
      driversStore: null as any, // remote grid store (server-side paging/sorting/filtering)
      stats: { active: 0, saved: 0, missing: 0, days: 0 }, // whole-company tiles, refreshed with every grid load
      driversError: '',
      loadingDriverId: null as number | null,
      uploadOpen: false,
      statusFilter: 'all',
      coverageFrom: '',
      coverageTo: '',
      activeTab: 'activity',
      viewMode: 'day',
      zoomByDate: {} as Record<string, { s: number; e: number }>, // per-day zoom window (minutes); absent = full day
      detailOpenByDate: {} as Record<string, boolean>, // per-day "detailed list" accordion state
      brushMode: null as null | 'pan' | 'left' | 'right',
      brushDate: '', // which day's slider is being dragged
      brushAnchorX: 0,
      brushWidthPx: 1,
      brushWin: { s: 0, e: 1440 },
      rangePreset: DEFAULT_PRESET.day,
      customFrom: '',
      customTo: '',
      selectedDayDate: '',
      hoveredWeekDate: '',
      placeAddressCache: {} as Record<string, string>,
      placeAddressLoading: {} as Record<string, boolean>,
      rangeReloadTimer: null as number | null,
      pendingStoredDriverReload: false,
    }
  },
  computed: {
    summary(): any {
      return this.result?.summary ?? {}
    },
    // Driver norm times computed by the backend over its own lookback window (independent
    // of the displayed range); null until the driver-data response arrives.
    times(): any {
      return this.result?.times ?? null
    },
    // Ferry/train crossings recorded by the card (SpecificConditions): gen-2 begin/end pairs
    // become ranges, unpaired entries become markers — the v1 analysis-view pairing.
    ferryTimeline(): { ranges: any[]; markers: any[] } {
      const conditions = (this.result?.conditions ?? []).filter((c: any) => c.Condition === 'ferry_train')
      const ranges: any[] = []
      const markers: any[] = []
      let begin: any = null
      for (const c of conditions) {
        if (c.Phase === 'begin') {
          if (begin) markers.push(begin)
          begin = c
        } else if (c.Phase === 'end' && begin) {
          ranges.push({ start: begin.Timestamp, end: c.Timestamp, generation: c.Generation })
          begin = null
        } else {
          markers.push(c)
        }
      }
      if (begin) markers.push(begin)
      return { ranges, markers }
    },
    hasFerryConditions(): boolean {
      const f = this.ferryTimeline
      return f.ranges.length > 0 || f.markers.length > 0
    },
    statusFilters(): any[] {
      const t = (k: string) => this.$t('components.tachoAnalysis.drivers.status.' + k)
      return [
        { key: 'all', label: t('all') },
        { key: 'saved', label: t('saved') },
        { key: 'missing', label: t('missing') },
      ]
    },
    // Newest day first — recent days (and CardPlaces, which only cover the most
    // recent period) are then visible immediately instead of buried under hundreds
    // of older day-rows.
    days(): any[] {
      return [...(this.result?.activities?.days ?? [])].reverse()
    },
    holderInitials(): string {
      const name = String(this.summary.holder_name ?? '').replace(/,/g, ' ').trim()
      if (!name) return '—'
      return name.split(/\s+/).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    },
    driverMetaItems(): any[] {
      const card = this.result?.identification?.card?.CardNumber
      const items: any[] = []
      if (card) items.push({ label: card, icon: 'bi-credit-card-2-front', mono: true })
      if (this.summary.card_type) items.push({ label: this.cardTypeLabel(this.summary.card_type), icon: 'bi-person-vcard' })
      if (this.summary.latest_download) items.push({ label: this.$t('components.tachoAnalysis.card.latestDownload') + ' ' + this.fmtDate(this.summary.latest_download), icon: 'bi-download' })
      return items.length ? items : [{ label: this.$t('components.tachoAnalysis.noCardData'), icon: 'bi-info-circle' }]
    },
    // Quick-range presets for the active view (week vs day).
    presets(): any[] {
      const t = (k: string) => this.$t('components.tachoAnalysis.presets.' + k)
      if (this.viewMode === 'week') {
        return [
          { key: '1w', label: t('1w'), days: 7 },
          { key: '2w', label: t('2w'), days: 14 },
          { key: '1mo', label: t('1mo'), days: 30 },
          { key: '3mo', label: t('3mo'), days: 90 },
        ]
      }
      return [
        { key: '7', label: t('7d'), days: 7 },
        { key: '28', label: t('28d'), days: 28 },
        { key: '90', label: t('90d'), days: 90 },
        { key: 'all', label: t('all'), days: 0 },
      ]
    },
    minDate(): string {
      const dates = this.days.map((d: any) => d.date)
      return dates.length ? [...dates].sort()[0] : ''
    },
    maxDate(): string {
      const dates = this.days.map((d: any) => d.date)
      return dates.length ? [...dates].sort().pop() : ''
    },
    // Effective range end/start (YYYY-MM-DD) for the active preset or custom selection.
    // The Od/Do inputs bind to these, so they always reflect the active quick choice.
    effectiveTo(): string {
      if (!this.days.length) return ''
      if (this.rangePreset === 'custom') return this.customTo || this.maxDate
      return this.maxDate // presets and "all" end at the newest day
    },
    effectiveFrom(): string {
      if (!this.days.length) return ''
      if (this.rangePreset === 'custom') return this.customFrom || this.minDate
      if (this.rangePreset === 'all') return this.minDate
      const days = this.presets.find((p: any) => p.key === this.rangePreset)?.days ?? 28
      return presetRangeStart(this.maxDate, days)
    },
    // Days narrowed to the effective range.
    filteredDays(): any[] {
      if (!this.days.length) return this.days
      const from = this.effectiveFrom
      const to = this.effectiveTo
      return (from && to) ? this.days.filter((d: any) => d.date >= from && d.date <= to) : this.days
    },
    selectedDay(): any {
      if (!this.filteredDays.length) return null
      return this.filteredDays.find((d: any) => d.date === this.selectedDayDate) ?? this.filteredDays[0]
    },
    selectedDayIndex(): number {
      if (!this.selectedDay) return -1
      return this.filteredDays.findIndex((d: any) => d.date === this.selectedDay.date)
    },
    rangeTotals(): any {
      return this.filteredDays.reduce((acc: any, day: any) => {
        const totals = day.totals ?? {}
        acc.drive += Number(totals.drive || 0)
        acc.work += Number(totals.work || 0)
        acc.available += Number(totals.available || 0)
        acc.rest += Number(totals.rest || 0)
        return acc
      }, { drive: 0, work: 0, available: 0, rest: 0 })
    },
    selectedDayTotals(): any[] {
      const totals = this.selectedDay?.totals ?? {}
      return [
        { key: 'drive', kind: 'drive', label: this.$t('components.tachoAnalysis.activities.drive'), value: this.hhmm(totals.drive || 0) },
        { key: 'work', kind: 'work', label: this.$t('components.tachoAnalysis.activities.work'), value: this.hhmm(totals.work || 0) },
        { key: 'available', kind: 'avail', label: this.$t('components.tachoAnalysis.activities.available'), value: this.hhmm(totals.available || 0) },
        { key: 'rest', kind: 'rest', label: this.$t('components.tachoAnalysis.activities.rest'), value: this.hhmm(totals.rest || 0) },
      ]
    },
    selectedDayVehicles(): any[] {
      return this.selectedDay ? this.dayVehicles(this.selectedDay) : []
    },
    selectedDayPlaces(): any[] {
      return this.selectedDay ? this.dayPlaces(this.selectedDay) : []
    },
    selectedDayEvents(): any[] {
      return this.selectedDay ? this.dayEvents(this.selectedDay) : []
    },
    // Render groups for the activity list: one flat group in day mode, or days grouped by
    // ISO week (newest first) with summed totals in week mode. Drives a single day-chart loop.
    displayGroups(): any[] {
      if (this.viewMode === 'day') {
        return [{ key: 'all', week: null, days: this.filteredDays }]
      }
      return groupDaysByIsoWeek(this.filteredDays)
    },
    tabs(): any[] {
      return [
        { key: 'activity', label: this.$t('components.tachoAnalysis.tabs.activity') },
        { key: 'vehicles', label: this.$t('components.tachoAnalysis.tabs.vehicles') },
        { key: 'places', label: this.$t('components.tachoAnalysis.tabs.places') },
        { key: 'events', label: this.$t('components.tachoAnalysis.tabs.events'), badge: this.eventsAll.length },
        { key: 'conditions', label: this.$t('components.tachoAnalysis.tabs.conditions'), badge: this.conditionsAll.length },
        { key: 'card', label: this.$t('components.tachoAnalysis.tabs.card') },
      ]
    },
    eventsAll(): any[] {
      const events = (this.result?.events ?? []).map((e: any) => this.mapEvent(e, false))
      const faults = (this.result?.faults ?? []).map((e: any) => this.mapEvent(e, true))
      return [...events, ...faults].sort((a, b) => String(b.sortKey).localeCompare(String(a.sortKey)))
    },
    conditionsAll(): any[] {
      return [...(this.result?.conditions ?? [])]
        .sort((a: any, b: any) => String(b.Timestamp ?? '').localeCompare(String(a.Timestamp ?? '')))
    },
    // Vehicle usage sessions (grouping + sparkline) — see utils/tacho/activity.
    vehicleSessions(): any[] {
      return vehicleSessions(this.result?.vehicles ?? [])
    },
    kmSum(): number {
      return (this.result?.vehicles ?? []).reduce((sum: number, v: any) => {
        return (v.OdometerBegin != null && v.OdometerEnd != null) ? sum + (v.OdometerEnd - v.OdometerBegin) : sum
      }, 0)
    },
    placesRows(): any[] {
      return (this.result?.places ?? []).map((p: any) => ({
        datetime: this.fmtDateTime(p.Timestamp),
        type: this.placeTypeLabel(p.EntryType),
        location: this.placeLocation(p),
        mapsUrl: this.placeMapsUrl(p),
        odo: p.Odometer != null ? this.group(p.Odometer) + ' km' : '—',
      }))
    },
    cardRows(): any[] {
      const t = (k: string) => this.$t('components.tachoAnalysis.card.' + k)
      const card = this.result?.identification?.card ?? {}
      const licence = this.result?.identification?.driving_licence ?? {}
      const rows = [
        { label: t('driver'), value: this.summary.holder_name },
        { label: t('cardNumber'), value: card.CardNumber, mono: true },
        { label: t('birthDate'), value: card.HolderBirthDate },
        { label: t('cardType'), value: this.summary.card_type ? this.cardTypeLabel(this.summary.card_type) : null },
        { label: t('issuingAuthority'), value: card.CardIssuingAuthorityName },
        { label: t('issueDate'), value: this.fmtDate(card.IssueDate) },
        { label: t('validFrom'), value: this.fmtDate(card.ValidityBegin) },
        { label: t('validTo'), value: this.fmtDate(card.ExpiryDate) },
        { label: t('licenceNation'), value: licence.IssuingNation },
        { label: t('licenceNumber'), value: licence.LicenceNumber, mono: true },
        { label: t('structureVersion'), value: this.summary.card_structure_version },
        { label: t('latestDownload'), value: this.fmtDate(this.summary.latest_download) },
        { label: t('dataRange'), value: this.days.length ? this.days.length + ' ' + this.$t('components.tachoAnalysis.daysUnit') : null },
      ]
      return rows.filter((r) => r.value !== null && r.value !== undefined && r.value !== '')
    },
    cardStatus(): any {
      const expiry = this.result?.identification?.card?.ExpiryDate
      if (!expiry) return null
      const m = moment.parseZone(expiry)
      if (!m.isValid()) return null
      return m.isAfter(moment())
        ? { kind: 'ok', label: this.$t('components.tachoAnalysis.card.valid') }
        : { kind: 'danger', label: this.$t('components.tachoAnalysis.card.expired') }
    },
  },
  watch: {
    // The custom filters live outside the grid, so a change must re-run its remote load.
    statusFilter() { if (!this.detailOnly) this.refreshDrivers() },
    coverageFrom() { if (!this.detailOnly) this.refreshDrivers() },
    coverageTo() { if (!this.detailOnly) this.refreshDrivers() },
    driverId() {
      if (this.detailOnly) this.loadDriverById(this.driverId)
    },
    activeTab(tab: string) {
      if (tab === 'places') this.prefetchPlaces(this.result?.places ?? [])
    },
    selectedDayDate() {
      this.prefetchPlaces(this.selectedDayPlaces)
    },
  },
  created() {
    this.hydratePlaceGeocodeCache()
    if (this.detailOnly) return
    this.driversStore = createDriversStore(
      () => ({
        status: this.statusFilter !== 'all' ? this.statusFilter : '',
        coverage_from: this.coverageFrom,
        coverage_to: this.coverageTo,
      }),
      (stats: any) => {
        this.stats = {
          active: Number(stats.active || 0),
          saved: Number(stats.saved || 0),
          missing: Number(stats.missing || 0),
          days: Number(stats.days || 0),
        }
        this.driversError = ''
      },
      (message: string) => {
        this.driversError = message || (this.$t('components.tachoAnalysis.drivers.loadFailed') as string)
      },
    )
  },
  mounted() {
    if (this.detailOnly) {
      this.loadDriverById(this.driverId)
    }
  },
  beforeUnmount() {
    if (this.rangeReloadTimer) window.clearTimeout(this.rangeReloadTimer)
    this.onBrushUp() // drop any lingering slider drag listeners
  },
  methods: {
    // --- Drivers panel -------------------------------------------------------
    driverFilterExpression(value: string) {
      if (!value) return null
      return [
        ['first_name', 'contains', value],
        'or',
        ['last_name', 'contains', value],
      ]
    },
    refreshDrivers() {
      (this.$refs.driversGrid as any)?.instance?.refresh()
    },
    // Open a driver's stored activity in a separate route.
    openDriver(row: any) {
      if (this.loadingDriverId) return
      this.$router.push({ name: 'TachoDriverActivity', params: { driverId: row.driver_id } })
    },
    openRemoteFiles(row: any) {
      (this.$refs.remoteFilesModal as any)?.open(row)
    },
    driverActivityParams(): Record<string, string | number> {
      if (this.rangePreset === 'all') return {}
      if (this.rangePreset === 'custom') {
        const params: Record<string, string> = {}
        if (this.customFrom) params.from = this.customFrom
        if (this.customTo) params.to = this.customTo
        return params
      }

      const days = this.presets.find((p: any) => p.key === this.rangePreset)?.days
      return days ? { days } : {}
    },
    scheduleStoredDriverReload(): void {
      if (!this.detailOnly || !this.driverId) return
      if (this.rangeReloadTimer) window.clearTimeout(this.rangeReloadTimer)
      this.rangeReloadTimer = window.setTimeout(() => {
        this.rangeReloadTimer = null
        this.loadDriverById(this.driverId, false)
      }, 250)
    },
    async loadDriverById(driverId: string | number | null, resetView = true) {
      const id = Number(driverId)
      if (!Number.isFinite(id) || id <= 0) {
        this.driversError = this.$t('components.tachoAnalysis.drivers.openFailed') as string
        return
      }
      if (this.loadingDriverId) {
        this.pendingStoredDriverReload = true
        return
      }
      this.loadingDriverId = id
      this.loading = true
      this.pendingStoredDriverReload = false
      this.driversError = ''
      try {
        const { data } = await axiosInstance.get(`/api/v1/tacho/activity/driver/${id}`, {
          params: this.driverActivityParams(),
        })
        this.result = data
        this.source = 'stored'
        this.storedMeta = data?.stored ?? null
        this.file = null
        this.ingest = null
        if (resetView) this.activeTab = 'activity'
        this.selectedDayDate = ''
        this.zoomByDate = {}
        this.detailOpenByDate = {}
        this.$nextTick(() => this.prefetchPlaces(this.selectedDayPlaces))
      } catch (e: any) {
        this.result = null
        this.source = null
        this.storedMeta = null
        this.driversError = e?.response?.data?.error || (this.$t('components.tachoAnalysis.drivers.openFailed') as string)
      } finally {
        this.loadingDriverId = null
        this.loading = false
        if (this.pendingStoredDriverReload) {
          this.pendingStoredDriverReload = false
          this.scheduleStoredDriverReload()
        }
      }
    },
    closeWorkspace() {
      if (this.detailOnly) {
        this.$router.push({ name: 'TachoFileAnalysis' })
        return
      }
      this.result = null
      this.source = null
      this.storedMeta = null
      this.file = null
      this.ingest = null
    },
    toggleUpload(open?: boolean | Event) {
      this.uploadOpen = true === open ? true : !this.uploadOpen
    },
    closeUpload() {
      this.uploadOpen = false
    },
    driverFullName(row: any): string {
      return [row.first_name, row.last_name].filter(Boolean).join(' ')
    },
    driverInitials(row: any): string {
      return ((String(row.first_name || '').charAt(0)) + (String(row.last_name || '').charAt(0))).toUpperCase() || '?'
    },
    fmtDay(day: string): string {
      return day ? moment(day, 'YYYY-MM-DD').format('DD.MM.YYYY') : '—'
    },
    // --- File upload -----------------------------------------------------------
    triggerFilePicker() {
      (this.$refs.fileInput as HTMLInputElement).click()
    },
    onFileChange(event: Event) {
      const input = event.target as HTMLInputElement
      this.selectFile(input.files?.[0] ?? null)
      input.value = '' // allow re-selecting the same file
    },
    onDragOver() {
      this.dragActive = true
    },
    onDragLeave(event: DragEvent) {
      // Ignore dragleave bubbling from child elements — only deactivate when leaving the zone.
      const zone = event.currentTarget as HTMLElement
      if (!zone.contains(event.relatedTarget as Node)) {
        this.dragActive = false
      }
    },
    onDrop(event: DragEvent) {
      this.dragActive = false
      this.selectFile(event.dataTransfer?.files?.[0] ?? null)
    },
    // Validate the chosen/dropped file is a .DDD, then import it into stored driver data.
    selectFile(file: File | null) {
      if (!file) return
      if (!/\.ddd$/i.test(file.name)) {
        this.result = null
        this.uploadOpen = true
        this.$toast.error(this.$t('components.tachoAnalysis.errors.badExtension') as string)
        return
      }
      this.file = file
      this.ingest = null
      this.ingestFile()
    },
    // Multipart payload for DDD ingest (the backend analyzes and stores canonically —
    // Europe/Warsaw, local days — so no presentation options ride along).
    uploadFormData(file: File): FormData {
      const formData = new FormData()
      formData.append('file', file)
      return formData
    },
    // Persist the current file's driver-card data (daily sums, intervals, vehicle sessions).
    // The backend rejects cards that are not assigned to a driver in the current company.
    async ingestFile() {
      if (!this.file || this.loading) return
      this.loading = true
      try {
        const { data } = await axiosInstance.post('/api/v1/tacho/file/ingest', this.uploadFormData(this.file))
        this.ingest = data?.ingest ?? null
        this.result = null
        this.source = null
        this.storedMeta = null
        this.uploadOpen = false
        this.$toast.success(this.$t(this.ingest?.duplicate ? 'components.tachoAnalysis.ingest.duplicate' : 'components.tachoAnalysis.ingest.saved') as string)
        this.refreshDrivers() // saved days change the drivers list coverage
      } catch (e: any) {
        // A success banner from an earlier save must not show next to this error.
        this.ingest = null
        this.$toast.error(this.uploadErrorMessage(e, 'components.tachoAnalysis.errors.saveFailed'))
      } finally {
        this.loading = false
      }
    },
    uploadErrorMessage(e: any, fallbackKey: string): string {
      const payload = e?.response?.data ?? {}
      if (payload.code === 'TACHO_DRIVER_NOT_IN_COMPANY') {
        return this.$t('components.tachoAnalysis.errors.driverNotInCompany') as string
      }

      return payload.error || (this.$t(fallbackKey) as string)
    },
    conditionKindLabel(kind: string): string {
      if (kind === 'ferry_train') return this.$t('components.tachoAnalysis.conditionKinds.ferryTrain') as string
      if (kind === 'out_of_scope') return this.$t('components.tachoAnalysis.conditionKinds.outOfScope') as string
      return kind || (this.$t('components.tachoAnalysis.conditionKinds.other') as string)
    },
    conditionPhaseLabel(phase: string): string {
      if (phase === 'begin') return this.$t('components.tachoAnalysis.conditionPhases.begin') as string
      if (phase === 'end') return this.$t('components.tachoAnalysis.conditionPhases.end') as string
      return this.$t('components.tachoAnalysis.conditionPhases.event') as string
    },
    cardTypeLabel(type: string): string {
      const known = ['CardTypeUndefined', 'CardTypeDriver', 'CardTypeWorkshop', 'CardTypeControl', 'CardTypeCompany', 'CardTypeManufacturing', 'CardTypeVehicleUnit']
      return known.includes(type)
        ? this.$t('components.tachoAnalysis.cardTypes.' + type) as string
        : type
    },
    // Manual Week/Day toggle: switch view and reset the range to that view's default.
    setViewMode(mode: 'day' | 'week') {
      if (this.viewMode === mode) return
      this.viewMode = mode
      this.rangePreset = mode === 'week' ? DEFAULT_PRESET.week : DEFAULT_PRESET.day
      this.customFrom = ''
      this.customTo = ''
      this.scheduleStoredDriverReload()
    },
    // Drill from the week chart into a single day: switch to Day view scoped to that date.
    goToDay(date: string) {
      this.viewMode = 'day'
      this.rangePreset = 'custom'
      this.customFrom = date
      this.customTo = date
      this.scheduleStoredDriverReload()
      this.$nextTick(() => {
        (this.$refs.activityCard as HTMLElement | undefined)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    setPreset(key: string) {
      this.rangePreset = key
      this.customFrom = ''
      this.customTo = ''
      this.scheduleStoredDriverReload()
    },
    // Editing either date field switches to a custom range, seeding the other end
    // from the currently effective range so the selection stays well-defined.
    onCustomFrom(event: Event) {
      const value = (event.target as HTMLInputElement).value
      this.customTo = this.customTo || this.effectiveTo
      this.customFrom = value
      this.rangePreset = 'custom'
      this.scheduleStoredDriverReload()
    },
    onCustomTo(event: Event) {
      const value = (event.target as HTMLInputElement).value
      this.customFrom = this.customFrom || this.effectiveFrom
      this.customTo = value
      this.rangePreset = 'custom'
      this.scheduleStoredDriverReload()
    },
    // --- Activity timeline -------------------------------------------------
    minuteOfDay(iso: string): number {
      return localMinuteOfDay(iso)
    },
    // --- Per-day chart zoom (window in minutes; absent = full day) ----------
    dayWindow(date: string): { s: number; e: number } {
      return this.zoomByDate[date] ?? { s: 0, e: DAY_MIN }
    },
    canZoomOutDay(date: string): boolean {
      const w = this.dayWindow(date)
      return w.e - w.s < DAY_MIN
    },
    // Main chart bars within the day's zoom window: geometry/kind from the service, plus the
    // duration label (same value as the table's duration, shown only when wide enough) + tooltip.
    dayBarsZoom(day: any): any[] {
      const w = this.dayWindow(day.date)
      return barsInWindow(day?.intervals ?? [], w.s, w.e).map((b: any) => ({
        ...b,
        label: this.hhmm(b.durationMin),
        showLabel: this.shouldShowBarLabel(b.width),
        tooltip: this.intervalTooltip({ activity: b.activity, start: b.startIso, end: b.endIso, duration_min: b.durationMin }, this.dowName(day.date) + ' ' + day.date),
        restLabel: this.restKindLabel(b.restKind),
      }))
    },
    // Slider overview bars (always the full day) under the draggable window.
    brushBars(day: any): any[] {
      return barsInWindow(day?.intervals ?? [], 0, DAY_MIN)
    },
    brushSel(date: string): Record<string, string> {
      const w = this.dayWindow(date)
      return { left: (w.s / DAY_MIN) * 100 + '%', width: ((w.e - w.s) / DAY_MIN) * 100 + '%' }
    },
    // Dark masks dimming the day outside the selected window (left + right of it).
    brushMaskLeft(date: string): Record<string, string> {
      return { left: '0', width: (this.dayWindow(date).s / DAY_MIN) * 100 + '%' }
    },
    brushMaskRight(date: string): Record<string, string> {
      const e = this.dayWindow(date).e
      return { left: (e / DAY_MIN) * 100 + '%', width: ((DAY_MIN - e) / DAY_MIN) * 100 + '%' }
    },
    // Slider drag: pan moves the window, handles resize it. Width is read from the
    // slider element (found via the event), so each day's slider works independently.
    onBrushDown(mode: 'pan' | 'left' | 'right', date: string, event: MouseEvent): void {
      this.brushMode = mode
      this.brushDate = date
      this.brushAnchorX = event.clientX
      this.brushWin = { ...this.dayWindow(date) }
      const brush = (event.currentTarget as HTMLElement).closest('.tfa-brush') as HTMLElement | null
      this.brushWidthPx = brush ? brush.getBoundingClientRect().width : 1
      window.addEventListener('mousemove', this.onBrushMove)
      window.addEventListener('mouseup', this.onBrushUp)
      event.preventDefault()
    },
    onBrushMove(event: MouseEvent): void {
      if (!this.brushMode) return
      const delta = ((event.clientX - this.brushAnchorX) / this.brushWidthPx) * DAY_MIN
      const { s, e } = this.brushWin
      const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi)
      let ns = s
      let ne = e
      if ('pan' === this.brushMode) {
        const width = e - s
        ns = clamp(s + delta, 0, DAY_MIN - width)
        ne = ns + width
      } else if ('left' === this.brushMode) {
        ns = clamp(s + delta, 0, e - MIN_ZOOM_WINDOW)
        ne = e
      } else {
        ne = clamp(e + delta, s + MIN_ZOOM_WINDOW, DAY_MIN)
        ns = s
      }
      this.zoomByDate = { ...this.zoomByDate, [this.brushDate]: { s: Math.round(ns), e: Math.round(ne) } }
    },
    onBrushUp(): void {
      this.brushMode = null
      window.removeEventListener('mousemove', this.onBrushMove)
      window.removeEventListener('mouseup', this.onBrushUp)
    },
    // Ferry/train is an annotation over an activity (usually rest), not a fifth activity
    // state. Build the same clipped segments for the day chart, sidebar, week and brush.
    dayFerryInWindow(day: any, w: { s: number; e: number }): { ranges: any[]; markers: any[] } {
      const span = w.e - w.s
      const clip = (startMin: number, endMin: number) => {
        const s = Math.max(startMin, w.s)
        const e = Math.min(endMin, w.e)
        return e > s
          ? { startMin: s, endMin: e, left: ((s - w.s) / span) * 100 + '%', width: ((e - s) / span) * 100 + '%' }
          : null
      }
      const ranges: any[] = []
      for (const r of this.ferryTimeline.ranges) {
        const sDate = String(r.start).slice(0, 10)
        const eDate = String(r.end).slice(0, 10)
        if (day.date < sDate || day.date > eDate) continue
        const pos = clip(sDate === day.date ? this.minuteOfDay(r.start) : 0, eDate === day.date ? this.minuteOfDay(r.end) : DAY_MIN)
        if (pos) {
          ranges.push({
            ...pos,
            tooltip: this.ferryTooltip(day, pos.startMin, pos.endMin, r.generation),
          })
        }
      }
      const markers: any[] = []
      for (const m of this.ferryTimeline.markers) {
        if (String(m.Timestamp).slice(0, 10) !== day.date) continue
        const minute = this.minuteOfDay(m.Timestamp)
        if (minute < w.s || minute > w.e) continue
        markers.push({
          minute,
          left: ((minute - w.s) / span) * 100 + '%',
          tooltip: this.ferryTooltip(day, minute, null, m.Generation, m.Phase),
        })
      }
      return { ranges, markers }
    },
    dayFerryZoom(day: any): { ranges: any[]; markers: any[] } {
      return this.dayFerryInWindow(day, this.dayWindow(day.date))
    },
    miniDayFerry(day: any): { ranges: any[]; markers: any[] } {
      return this.dayFerryInWindow(day, { s: 0, e: DAY_MIN })
    },
    ferryTooltip(day: any, startMin: number, endMin: number | null, generation: number, phase = ''): any {
      const isRange = endMin !== null
      return {
        label: this.$t('components.tachoAnalysis.conditionKinds.ferryTrain'),
        detail: `${this.$t('components.tachoAnalysis.ferry.cardCondition')} · ${this.$t('components.tachoAnalysis.conditionGeneration', { generation })}`,
        range: isRange ? `${this.minutesToHM(startMin)} – ${this.minutesToHM(endMin as number)}` : this.minutesToHM(startMin),
        duration: isRange ? this.hhmm((endMin as number) - startMin) : this.conditionPhaseLabel(phase),
        day: `${this.dowName(day.date)} ${day.date}`,
      }
    },
    // Place markers within the day's zoom window (those outside are dropped).
    dayPlaceLinesZoom(day: any): any[] {
      const w = this.dayWindow(day.date)
      const span = w.e - w.s
      return this.dayPlaces(day)
        .filter((p: any) => p._minute >= w.s && p._minute <= w.e)
        .map((p: any) => ({
          left: ((p._minute - w.s) / span) * 100 + '%',
          kind: this.placeMarkerKind(p),
          label: this.placeShortLabel(p),
          tooltip: this.placeTooltip(p, this.dowName(day.date) + ' ' + day.date),
        }))
    },
    // Evenly spaced time ticks across the day's zoom window.
    dayAxis(day: any): any[] {
      const w = this.dayWindow(day.date)
      const steps = 4
      const out: any[] = []
      for (let k = 0; k <= steps; k++) {
        out.push({ label: this.minutesToHM(w.s + ((w.e - w.s) * k) / steps) })
      }
      return out
    },
    selectDay(date: string): void {
      this.selectedDayDate = date
      this.$nextTick(() => this.prefetchPlaces(this.selectedDayPlaces))
    },
    canMoveSelectedDay(delta: number): boolean {
      const next = this.selectedDayIndex + delta
      return next >= 0 && next < this.filteredDays.length
    },
    moveSelectedDay(delta: number): void {
      if (!this.canMoveSelectedDay(delta)) return
      this.selectedDayDate = this.filteredDays[this.selectedDayIndex + delta].date
      this.$nextTick(() => this.prefetchPlaces(this.selectedDayPlaces))
    },
    momentDayNumber(date: string): number {
      return moment(date, 'YYYY-MM-DD').day()
    },
    miniDayBars(day: any): any[] {
      return barsInWindow(day?.intervals ?? [], 0, DAY_MIN)
    },
    miniDayPlaces(day: any): any[] {
      return this.dayPlaces(day).map((place: any) => ({
        left: Math.min(99, Math.max(1, (place._minute / DAY_MIN) * 100)) + '%',
        kind: this.placeMarkerKind(place),
        label: this.placeShortLabel(place),
        title: this.placeTooltip(place, this.dowName(day.date) + ' ' + day.date).title,
      }))
    },
    toggleDetail(date: string): void {
      this.detailOpenByDate = { ...this.detailOpenByDate, [date]: !this.detailOpenByDate[date] }
    },
    isDetailOpen(date: string): boolean {
      return !!this.detailOpenByDate[date]
    },
    minutesToHM(minutes: number): string { return clock(minutes) },
    // --- Week-combined chart (week view) -----------------------------------
    // Day offset (0 = Monday) of a date within its ISO week, for positioning on the 7-day span.
    weekDayOffset(week: any, dateIso: string): number {
      return moment(dateIso, 'YYYY-MM-DD').diff(moment(week.mondayIso, 'YYYY-MM-DD'), 'days')
    },
    // All intervals of the week's days, positioned across the full Mon–Sun span.
    weekBars(week: any, days: any[]): any[] {
      const bars: any[] = []
      for (const day of days) {
        const offsetMin = this.weekDayOffset(week, day.date) * 1440
        for (const i of day.intervals ?? []) {
          const startMin = offsetMin + this.minuteOfDay(i.start)
          bars.push({
            left: (startMin / WEEK_MIN) * 100 + '%',
            width: (i.duration_min / WEEK_MIN) * 100 + '%',
            kind: activityKind(i.activity),
            date: day.date,
            label: this.hhmm(i.duration_min),
            showLabel: this.shouldShowBarLabel((i.duration_min / WEEK_MIN) * 100 + '%'),
            tooltip: this.intervalTooltip(i, this.dowName(day.date) + ' ' + day.date),
            restKind: i.rest_kind ?? null,
            restLabel: this.restKindLabel(i.rest_kind),
          })
        }
      }
      return bars
    },
    weekFerry(week: any, days: any[]): { ranges: any[]; markers: any[] } {
      const ranges: any[] = []
      const markers: any[] = []
      for (const day of days) {
        const offsetMin = this.weekDayOffset(week, day.date) * DAY_MIN
        const ferry = this.miniDayFerry(day)
        for (const range of ferry.ranges) {
          ranges.push({
            ...range,
            date: day.date,
            left: ((offsetMin + range.startMin) / WEEK_MIN) * 100 + '%',
            width: ((range.endMin - range.startMin) / WEEK_MIN) * 100 + '%',
          })
        }
        for (const marker of ferry.markers) {
          markers.push({
            ...marker,
            date: day.date,
            left: ((offsetMin + marker.minute) / WEEK_MIN) * 100 + '%',
          })
        }
      }
      return { ranges, markers }
    },
    // CardPlace markers across the week span.
    weekPlaceLines(week: any, days: any[]): any[] {
      const lines: any[] = []
      for (const day of days) {
        const offsetMin = this.weekDayOffset(week, day.date) * 1440
        for (const p of this.dayPlaces(day)) {
          lines.push({
            left: ((offsetMin + p._minute) / WEEK_MIN) * 100 + '%',
            date: day.date,
            kind: this.placeMarkerKind(p),
            label: this.placeShortLabel(p),
            tooltip: this.placeTooltip(p, this.dowName(day.date) + ' ' + day.date),
          })
        }
      }
      return lines
    },
    setWeekHover(date: string): void {
      this.hoveredWeekDate = date
    },
    clearWeekHover(date: string): void {
      if (this.hoveredWeekDate === date) this.hoveredWeekDate = ''
    },
    // The 7 day columns (Mon–Sun): position, label, and a clickable handle for present days.
    weekColumns(week: any, days: any[]): any[] {
      const byDate: Record<string, any> = {}
      days.forEach((d: any) => { byDate[d.date] = d })
      const monday = moment(week.mondayIso, 'YYYY-MM-DD')
      const cols: any[] = []
      for (let k = 0; k < 7; k++) {
        const m = monday.clone().add(k, 'days')
        const dateIso = m.format('YYYY-MM-DD')
        const day = byDate[dateIso]
        cols.push({
          index: k,
          date: dateIso,
          left: (k / 7) * 100 + '%',
          width: (100 / 7) + '%',
          present: !!day,
          dow: this.dowAbbr(m.day()),
          dom: m.format('DD.MM'),
          tooltip: day ? {
            date: dateIso,
            dow: this.dowName(dateIso),
            drive: this.hhmm(day.totals?.drive || 0),
            work: this.hhmm(day.totals?.work || 0),
            driveWork: this.hhmm((day.totals?.drive || 0) + (day.totals?.work || 0)),
          } : null,
        })
      }
      return cols
    },
    // Chronological breakdown for the inline detail table; flags segments that contain a CardPlace entry.
    segmentRows(day: any): any[] {
      const placeMinutes = this.dayPlaces(day).map((p: any) => p._minute)
      const ferry = this.miniDayFerry(day)
      return (day?.intervals ?? []).map((i: any) => {
        const startMin = this.minuteOfDay(i.start)
        const endMin = startMin + i.duration_min
        const ferryParts = [
          ...ferry.ranges
            .filter((range: any) => range.startMin < endMin && range.endMin > startMin)
            .map((range: any) => range.tooltip.range),
          ...ferry.markers
            .filter((marker: any) => marker.minute >= startMin && marker.minute <= endMin)
            .map((marker: any) => marker.tooltip.range),
        ]
        return {
          start: this.fmtTime(i.start),
          stop: this.fmtTime(i.end),
          label: this.activityLabel(i.activity),
          kind: activityKind(i.activity),
          restKind: i.rest_kind ?? null,
          duration_min: i.duration_min,
          hasPlace: placeMinutes.some((m: number) => m >= startMin && m <= endMin),
          ferry: ferryParts.length
            ? `${this.$t('components.tachoAnalysis.conditionKinds.ferryTrain')} ${ferryParts.join(', ')}`
            : '',
        }
      })
    },
    // CardPlaces belonging to a day, aligned to its local frame — see utils/tacho/activity.
    dayPlaces(day: any): any[] {
      return alignPlacesToDay(this.result?.places ?? [], day)
    },
    dayVehicles(day: any): any[] {
      const overlapping = (this.result?.vehicles ?? []).filter((vehicle: any) => {
        const first = this.isoDatePart(vehicle.FirstUse)
        const last = this.isoDatePart(vehicle.LastUse)
        if (!first && !last) return false
        const from = first ?? last ?? ''
        const to = last ?? first ?? ''
        return from <= day.date && day.date <= to
      })

      // Gen-1 CardVehiclesUsed records commonly follow UTC file-day boundaries. After
      // conversion to Warsaw a record is 01:00 -> 00:59 on the next date, so the raw
      // inclusive date test sees both the previous and current record. Group by vehicle and
      // prefer records beginning on the selected nominal day (the same date DayDistance uses).
      // If a genuine multi-day session has no new record that day, keep the overlapping row.
      const groups = new Map<string, any[]>()
      for (const vehicle of overlapping) {
        const key = [vehicle.RegistrationNumber, vehicle.RegistrationNation, vehicle.IdentificationNumber]
          .map((value) => String(value ?? ''))
          .join('|')
        const rows = groups.get(key) ?? []
        rows.push(vehicle)
        groups.set(key, rows)
      }

      const merged = [...groups.values()].map((rows: any[]) => {
        const startingToday = rows.filter((row: any) => this.isoDatePart(row.FirstUse) === day.date)
        const selected = (startingToday.length ? startingToday : rows)
          .slice()
          .sort((a: any, b: any) => String(a.FirstUse ?? '').localeCompare(String(b.FirstUse ?? '')))
        const first = selected[0]
        const last = selected[selected.length - 1]
        return {
          ...first,
          LastUse: last.LastUse,
          OdometerEnd: last.OdometerEnd,
        }
      })

      // DayDistance is the authoritative distance for a nominal card day. It can be assigned
      // to the row only when exactly one vehicle was used; with multiple vehicles their raw
      // session deltas remain separate rather than duplicating the day's total.
      if (merged.length === 1 && day.day_distance_km != null) {
        merged[0]._dayDistance = Number(day.day_distance_km)
      }

      return merged
    },
    vehicleDistance(vehicle: any): number | null {
      if (vehicle?._dayDistance != null) {
        const distance = Number(vehicle._dayDistance)
        return Number.isFinite(distance) && distance >= 0 ? distance : null
      }
      if (vehicle?.OdometerBegin == null || vehicle?.OdometerEnd == null) return null
      const distance = Number(vehicle.OdometerEnd) - Number(vehicle.OdometerBegin)
      return Number.isFinite(distance) && distance >= 0 ? distance : null
    },
    vehicleDistanceLabel(vehicle: any): string {
      const distance = this.vehicleDistance(vehicle)
      return distance === null ? '' : this.group(distance) + ' km'
    },
    odometerLabel(value: any): string {
      return value == null ? '—' : this.group(value)
    },
    dayEvents(day: any): any[] {
      const rows: any[] = []
      ;(this.result?.events ?? []).forEach((event: any) => rows.push({ ...this.mapEvent(event, false), raw: event }))
      ;(this.result?.faults ?? []).forEach((fault: any) => rows.push({ ...this.mapEvent(fault, true), raw: fault }))
      return rows.filter((row: any) => {
        const begin = this.isoDatePart(row.raw?.BeginTime)
        const end = this.isoDatePart(row.raw?.EndTime)
        if (begin && end) return begin <= day.date && day.date <= end
        return begin === day.date || end === day.date
      })
    },
    isoDatePart(value: any): string | null {
      return typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : null
    },
    // Daily-work-period entry type (CardPlaces EntryTypeDailyWorkPeriod): even = begin, odd = end.
    placeTypeLabel(code: number): string {
      const known = [0, 1, 2, 3]
      if (known.includes(code)) return this.$t('components.tachoAnalysis.placeTypes.' + code) as string
      return code != null ? this.$t('components.tachoAnalysis.placeTypes.other', { code }) as string : '—'
    },
    placeMarkerKind(place: any): 'start' | 'end' {
      return Number(place?.EntryType) % 2 === 0 ? 'start' : 'end'
    },
    placeShortLabel(place: any): string {
      return this.$t('components.tachoAnalysis.placeShort.' + this.placeMarkerKind(place)) as string
    },
    hydratePlaceGeocodeCache(): void {
      if (typeof window === 'undefined') return
      try {
        const raw = window.localStorage.getItem(PLACE_GEOCODE_CACHE_KEY)
        const parsed = raw ? JSON.parse(raw) : {}
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return

        Object.entries(parsed).forEach(([key, value]) => {
          if (typeof value === 'string' && value) placeGeocodeMemory.set(key, value)
        })
        this.placeAddressCache = Object.fromEntries(placeGeocodeMemory.entries())
      } catch {
        this.placeAddressCache = Object.fromEntries(placeGeocodeMemory.entries())
      }
    },
    persistPlaceGeocodeCache(): void {
      if (typeof window === 'undefined') return
      try {
        const entries = [...placeGeocodeMemory.entries()].slice(-PLACE_GEOCODE_MAX_CACHE)
        placeGeocodeMemory.clear()
        entries.forEach(([key, value]) => placeGeocodeMemory.set(key, value))
        window.localStorage.setItem(PLACE_GEOCODE_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)))
      } catch {
        // localStorage can be unavailable or full; in-memory cache still prevents duplicate calls in this session.
      }
    },
    placeGeoKey(place: any): string {
      const lat = Number(place?.GNSSLatitude)
      const lng = Number(place?.GNSSLongitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return ''
      return `${lat.toFixed(5)},${lng.toFixed(5)}`
    },
    placeMapsUrl(place: any): string {
      const key = this.placeGeoKey(place)
      return key ? `https://www.google.com/maps/search/?api=1&query=${key}` : ''
    },
    placeFallbackLocation(p: any): string {
      const parts: any[] = [p.Country]
      if (p.GNSSLatitude != null && p.GNSSLongitude != null) {
        parts.push(`${p.GNSSLatitude}, ${p.GNSSLongitude}`)
      }
      return parts.filter(Boolean).join(' · ') || '—'
    },
    placeLocation(p: any): string {
      const key = this.placeGeoKey(p)
      if (key && this.placeAddressCache[key]) return this.placeAddressCache[key]
      if (key && placeGeocodeMemory.has(key)) return placeGeocodeMemory.get(key) as string
      return this.placeFallbackLocation(p)
    },
    async ensurePlaceGeocoded(place: any): Promise<void> {
      const key = this.placeGeoKey(place)
      if (!key || this.placeAddressCache[key] || this.placeAddressLoading[key]) return

      if (placeGeocodeMemory.has(key)) {
        this.placeAddressCache = { ...this.placeAddressCache, [key]: placeGeocodeMemory.get(key) as string }
        return
      }

      this.placeAddressLoading = { ...this.placeAddressLoading, [key]: true }
      try {
        const response = await getReverseGeocode(key)
        const address = typeof response?.data === 'string' ? response.data : ''
        if (address) {
          placeGeocodeMemory.set(key, address)
          this.placeAddressCache = { ...this.placeAddressCache, [key]: address }
          this.persistPlaceGeocodeCache()
        }
      } catch {
        // Keep the raw country/coordinates fallback; geocoding is a progressive enhancement here.
      } finally {
        const next = { ...this.placeAddressLoading }
        delete next[key]
        this.placeAddressLoading = next
      }
    },
    async prefetchPlaces(places: any[]): Promise<void> {
      const unique = new Map<string, any>()
      for (const place of places ?? []) {
        const key = this.placeGeoKey(place)
        if (key && !this.placeAddressCache[key] && !placeGeocodeMemory.has(key)) unique.set(key, place)
      }

      for (const place of unique.values()) {
        await this.ensurePlaceGeocoded(place)
      }
    },
    placeTooltip(place: any, dayLabel: string): any {
      const type = this.placeTypeLabel(place.EntryType)
      const time = place._time || this.fmtTime(place.Timestamp)
      const location = this.placeLocation(place)
      const odo = place.Odometer != null
        ? this.$t('components.tachoAnalysis.odometerCap') + ' ' + this.group(place.Odometer) + ' km'
        : this.$t('components.tachoAnalysis.odometerCap') + ' —'
      return {
        type,
        time,
        location,
        odo,
        day: dayLabel,
        title: `${type}, ${time} · ${location} · ${odo}`,
      }
    },
    mapEvent(record: any, isFault: boolean): any {
      const begin = record.BeginTime ?? null
      const severity = isFault ? 'warn' : eventSeverity(record.Type)
      return {
        date: begin ? moment.parseZone(begin).format('DD.MM') : '—',
        time: begin ? this.fmtTime(begin) : '',
        sortKey: begin ?? '',
        title: this.eventTypeLabel(record, isFault),
        detail: record.RegistrationNumber || '',
        severity,
        duration: this.eventDuration(begin, record.EndTime ?? null),
      }
    },
    eventTypeLabel(record: any, isFault: boolean): string {
      const type = String(record.Type ?? '')
      const translationKey = `components.tachoAnalysis.eventTypes.${type}`
      if (type && this.$te(translationKey)) return this.$t(translationKey) as string

      if (type && !/^\d+$/.test(type)) {
        return type.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      }

      const fallback = this.$t(`components.tachoAnalysis.${isFault ? 'fault' : 'event'}`) as string
      const code = Number(record.TypeCode ?? type)
      return Number.isFinite(code)
        ? `${fallback} (${this.$t('components.tachoAnalysis.eventCode', { code })})`
        : fallback
    },
    eventDuration(begin: string | null, end: string | null): string {
      if (!begin || !end) return '—'
      const start = moment.parseZone(begin)
      const finish = moment.parseZone(end)
      if (!start.isValid() || !finish.isValid()) return '—'
      const seconds = Math.max(0, finish.diff(start, 'seconds'))
      if (seconds > 0 && seconds < 60) {
        return this.$t('components.tachoAnalysis.durationSeconds', { seconds }) as string
      }
      return this.durationBetween(begin, end)
    },
    activityLabel(activity: string): string {
      const key = { Drive: 'drive', Work: 'workLong', Available: 'available', Rest: 'restLong' }[activity]
      return key ? this.$t('components.tachoAnalysis.activities.' + key) as string : (activity || '—')
    },
    // Backend 561/2006 rest classification (rest_kind) -> translated label; '' hides the row.
    restKindLabel(kind: string | null | undefined): string {
      if (!kind) return ''
      return this.$t('components.tachoAnalysis.restKinds.' + kind) as string
    },
    // Structured activity tooltip content used by daily and weekly chart bars.
    intervalTooltip(interval: any, dayLabel: string): any {
      return {
        label: this.activityLabel(interval.activity),
        range: `${this.fmtTime(interval.start)} – ${this.fmtTime(interval.end)}`,
        duration: this.hhmm(interval.duration_min),
        day: dayLabel,
      }
    },
    shouldShowBarLabel(width: string): boolean {
      return parseFloat(width) >= 1.3
    },
    // --- Formatting helpers ------------------------------------------------
    display(value: any): string {
      if (value === null || value === undefined || value === '') return '—'
      if (typeof value === 'boolean') return value ? this.$t('common.yes') as string : this.$t('common.no') as string
      return String(value)
    },
    // Formatting helpers — thin wrappers over utils/tacho/format so templates can call them.
    hhmm(minutes: number): string { return hoursMinutes(minutes) },
    fmtTime(iso: string): string { return formatTime(iso) },
    fmtDate(iso: string): string { return formatDateFull(iso) },
    fmtDateTime(iso: string): string { return formatDateTime(iso) },
    durationBetween(begin: string | null, end: string | null): string { return durationBetween(begin, end) },
    group(value: any): string { return groupThousands(value) },
    dowName(date: string): string {
      const m = moment(date, 'YYYY-MM-DD')
      return m.isValid() ? this.$t('components.tachoAnalysis.dow.' + m.day()) as string : ''
    },
    dowAbbr(day: number): string {
      return this.$t('components.tachoAnalysis.dowAbbr.' + day) as string
    },
  },
})
</script>

<!-- Unscoped on purpose: the sheet is fully namespaced under #tacho-file-analysis, and
     scoped CSS would not reach child components (the downloaded-files modal) nor
     DevExtreme-rendered grid internals. -->
<style lang="scss" src="@/assets/scss/tacho-file-analysis.scss"></style>
